import * as React from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";

export const SortableTableHeader = React.forwardRef(
  ({ className, sortKey, sortConfig, onSort, children, ...props }, ref) => {
    const getSortIcon = () => {
      if (!sortConfig || sortConfig.key !== sortKey) {
        return <ChevronsUpDown className="h-4 w-4" />;
      }
      return sortConfig.direction === 'asc' 
        ? <ChevronUp className="h-4 w-4" />
        : <ChevronDown className="h-4 w-4" />;
    };

    return (
      <th
        ref={ref}
        className={cn(
          "h-12 px-4 text-left align-middle font-medium text-gray-700 cursor-pointer select-none",
          className
        )}
        onClick={() => onSort(sortKey)}
        {...props}
      >
        <div className="flex items-center space-x-2">
          <span>{children}</span>
          {getSortIcon()}
        </div>
      </th>
    );
  }
);
SortableTableHeader.displayName = "SortableTableHeader";

export const useSortableData = (data, defaultSortKey, defaultDirection = 'asc') => {
  const [sortConfig, setSortConfig] = React.useState(
    defaultSortKey ? { key: defaultSortKey, direction: defaultDirection } : null
  );

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1; // null values go to end
      if (bValue == null) return -1;

      // Handle date objects and date strings
      if (aValue instanceof Date || (typeof aValue === 'string' && !isNaN(Date.parse(aValue)))) {
        aValue = aValue instanceof Date ? aValue : new Date(aValue);
        bValue = bValue instanceof Date ? bValue : new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(current => {
      if (!current || current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  return { sortedData, sortConfig, handleSort };
};